import './ClientApplicationsSummary.css';
import ExcelJS from 'exceljs';
import { useEffect, useState } from 'react';
import CanvasJSReact from '@canvasjs/react-charts';
import { preferenceOptions } from '../../utilities/services/preferencesOptions';
import { getClientPatentApplications } from '../../utilities/api/patentApplications';
import { getClientTrademarkApplications } from '../../utilities/api/trademarkApplications';
import ClientApplicationsSummaryReminderItem from '../ClientApplicationsSummaryReminderItem/ClientApplicationsSummaryReminderItem';
import { capitalizeFirstLetter } from '../../utilities/services/capitalizeFirstLetter';


const CanvasJSChart = CanvasJSReact.CanvasJSChart;

export default function ClientApplicationsSummary({user, client, applicationsData, services, handleDropdownClick}) {
  const [annualData, setAnnualData] = useState([])
  const [applicationsDistribution, setApplicationsDistribution] = useState([])
  const [patentApplicationsByType, setPatentApplicationsByType] = useState([])
  const [trademarkApplicationsByType, setTrademarkApplicationsByType] = useState([])
  const [isExportingSheet, setIsExportingSheet] = useState(false)
  const [shouldRenderCharts, setShouldRenderCharts] = useState(false)

  // console.log('applicationsData', applicationsData)

  const applicationsDistributionDonutOptions = {
    animationEnabled: true,
    title: {
      // text: "Applications Distribution"
    },
    subtitles: [{
      text: "Distribution",
      verticalAlign: "center",
      fontSize: 24,
      dockInsidePlotArea: true
    }],
    data: [{
      type: "doughnut",
      showInLegend: true,
      indexLabel: "{name}: {y}",
      yValueFormatString: "#,###'%'",
      dataPoints: applicationsDistribution
    }]
  }

  const annualDataAreaChartOptions = {
    animationEnabled: true,
    theme: "light2",
    title: {
        // text: "Applicants Added Monthly" 
    },
    axisX: {
        valueFormatString: "MMM",
        intervalType: "month",
        interval: 1
    },
    axisY: {
        title: "Quantity",
    },
    toolTip: {
      shared: true,
      contentFormatter: function (e) {
        let content = ''
        e.entries.forEach(entry => {
          content += `<strong>${entry.dataSeries.name}</strong>: ${entry.dataPoint.y} ${entry.dataPoint.y === 1 ? 'Application' : 'Applications'}<br>`
        })
        return content
      }
    },
    legend: {
        cursor: "pointer",
        itemclick: function (e) {
            if (typeof (e.dataSeries.visible) === "undefined" || e.dataSeries.visible) {
                e.dataSeries.visible = false;
            } else {
                e.dataSeries.visible = true;
            }
            e.chart.render();
        }
    },
    data: annualData
  };

  const patentApplicationsByTypePieChartOptions = {
    exportEnabled: true,
    animationEnabled: true,
    title: {
      // text: "Patent Applications by Type"
    },
    data: [{
      type: "pie",
      startAngle: 75,
      toolTipContent: "<b>{label}</b>: {y}%",
      showInLegend: "true",
      legendText: "{label}",
      indexLabelFontSize: 14,
      indexLabel: "{label} - {y}%",
      dataPoints: [
        ...patentApplicationsByType
      ]
    }]
  }

  const trademarkApplicationsByTypePieChartOptions = {
    exportEnabled: true,
    animationEnabled: true,
    title: {
      // text: "Trademark Applications by Type"
    },
    data: [{
      type: "pie",
      startAngle: 75,
      toolTipContent: "<b>{label}</b>: {y}%",
      showInLegend: "true",
      legendText: "{label}",
      indexLabelFontSize: 14,
      indexLabel: "{label} - {y}%",
      dataPoints: [
        ...trademarkApplicationsByType
      ]
    }]
  }

  const createExcelSheet = async () => {
    setIsExportingSheet(true)
    const patentColumns = []
    const trademarkColumns = []
    try {
      const patentApplications = await getClientPatentApplications(user, client.clientID)
      const trademarkApplications = await getClientTrademarkApplications(user, client.clientID)

      const workbook = new ExcelJS.Workbook();

      // Create a new worksheet for Patent Applications
      const sheet1 = workbook.addWorksheet('Patent Applications');
      Object.keys(preferenceOptions['patentApplications']).map((option) => {
        return patentColumns.push({header: preferenceOptions['patentApplications'][option], key: option, width: 20})
      })
      sheet1.columns = patentColumns
      sheet1.getRow(1).font = { bold: true, size: 14 };

      patentApplications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      patentApplications.forEach((application) => {
        const row = {}
        Object.keys(preferenceOptions['patentApplications']).map((option) => {
          if (application[option]) {
            return row[option] = application[option]
          } else {
            return row[option] = ''
          }
        })
        sheet1.addRow(row)
      })

      // Create a new worksheet for Trademark Applications
      const sheet2 = workbook.addWorksheet('Trademark Applications');
      Object.keys(preferenceOptions['trademarkApplications']).map((option) => {
        return trademarkColumns.push({header: preferenceOptions['trademarkApplications'][option], key: option, width: 20})
      })
      sheet2.columns = trademarkColumns
      sheet2.getRow(1).font = { bold: true, size: 14 };

      trademarkApplications.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      trademarkApplications.forEach((application) => {
        const row = {}
        Object.keys(preferenceOptions['trademarkApplications']).map((option) => {
          if (application[option]) {
            return row[option] = application[option]
          } else {
            return row[option] = ''
          }
        })
        sheet2.addRow(row)
      })

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${capitalizeFirstLetter(client.companyName)} Applications.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.log(error)
    }
    setIsExportingSheet(false)
  }

  useEffect(() => {
    setShouldRenderCharts(false)
    // Process Annual Data
    const currentDate = new Date();
    const tempAnnualData = applicationsData.annualData.map((data) => {
      return {
        type: "line",
        name: (data.year).toString(),
        showInLegend: true,
        xValueFormatString: "MMMM",
        yValueFormatString: "#,##0",
        // Use default year of 2025, so that line charts are aligned
        dataPoints: [
          { date: new Date(data.year, 0), x: new Date(2025, 0), y: data.monthlyData.JAN },
          { date: new Date(data.year, 1), x: new Date(2025, 1), y: data.monthlyData.FEB },
          { date: new Date(data.year, 2), x: new Date(2025, 2), y: data.monthlyData.MAR },
          { date: new Date(data.year, 3), x: new Date(2025, 3), y: data.monthlyData.APR },
          { date: new Date(data.year, 4), x: new Date(2025, 4), y: data.monthlyData.MAY },
          { date: new Date(data.year, 5), x: new Date(2025, 5), y: data.monthlyData.JUN },
          { date: new Date(data.year, 6), x: new Date(2025, 6), y: data.monthlyData.JUL },
          { date: new Date(data.year, 7), x: new Date(2025, 7), y: data.monthlyData.AUG },
          { date: new Date(data.year, 8), x: new Date(2025, 8), y: data.monthlyData.SEP },
          { date: new Date(data.year, 9), x: new Date(2025, 9), y: data.monthlyData.OCT },
          { date: new Date(data.year, 10), x: new Date(2025, 10), y: data.monthlyData.NOV },
          { date: new Date(data.year, 11), x: new Date(2025, 11), y: data.monthlyData.DEC }
        ].filter(dataPoint => currentDate >= dataPoint.date).map(dataPoint => {
          return {x: dataPoint.x, y: dataPoint.y}
        })
      }
    })
    setAnnualData(tempAnnualData)

    // Process Applications Distribution
    const totalApplications = applicationsData.totalPatentApplications + applicationsData.totalTrademarkApplications
    const tempApplicationDistribution = [
      { name: 'Patents', y: Math.round((applicationsData.totalPatentApplications *100)/totalApplications), color: "#E21D12" },
      { name: 'Trademarks', y: Math.round((applicationsData.totalTrademarkApplications *100)/totalApplications), color: "#1A5E9B" },
    ]
    setApplicationsDistribution(tempApplicationDistribution)

    // Process patent applications by type
    const tempPatentApplicationsByType = Object.keys(applicationsData.patentApplicationsByType).map(type => {
      return {
        label: type.toUpperCase(),
        y: Math.round((applicationsData.patentApplicationsByType[type]*100)/(applicationsData.totalPatentApplications))
      }
    })
    setPatentApplicationsByType(tempPatentApplicationsByType)

    // Process trademark applications by type
    const tempTrademarkApplicationsByType = Object.keys(applicationsData.trademarkApplicationsByType).map(type => {
      return {
        label: type.toUpperCase(),
        y: Math.round((applicationsData.trademarkApplicationsByType[type]*100)/(applicationsData.totalTrademarkApplications))
      }
    })
    setTrademarkApplicationsByType(tempTrademarkApplicationsByType)

    // Add delay for chart rendering
    setTimeout(() => {
      setShouldRenderCharts(true)
    }, 100)

  }, [applicationsData])
  return (
    <div className='ClientApplicationsSummary'>
      <div className='exportApplicationsContainer dropdown'>
        <button className='exportApplications' title='Export Applications to Spreadsheet' id='exportApplicationsDropDownToggle' data-title='Export Applications' data-bs-toggle="dropdown" data-bs-auto-close='outside' aria-expanded="false">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50 50" width="20px" fill='#FFFFFF'>
            <path d="M 28.90625 1.96875 C 28.863281 1.976563 28.820313 1.988281 28.78125 2 L 11.5 2 C 9.585938 2 8 3.558594 8 5.46875 L 8 43.90625 C 8 46.160156 9.867188 48 12.125 48 L 37.875 48 C 40.132813 48 42 46.160156 42 43.90625 L 42 15.1875 C 42.027344 15.054688 42.027344 14.914063 42 14.78125 L 42 14.5 C 42.007813 14.234375 41.90625 13.972656 41.71875 13.78125 L 30.21875 2.28125 C 30.027344 2.09375 29.765625 1.992188 29.5 2 L 29.1875 2 C 29.097656 1.976563 29 1.964844 28.90625 1.96875 Z M 11.5 4 L 28 4 L 28 12.34375 C 28 14.355469 29.644531 16 31.65625 16 L 40 16 L 40 43.90625 C 40 45.074219 39.054688 46 37.875 46 L 12.125 46 C 10.945313 46 10 45.074219 10 43.90625 L 10 5.46875 C 10 4.644531 10.660156 4 11.5 4 Z M 30 4.9375 L 39.0625 14 L 31.65625 14 C 30.722656 14 30 13.277344 30 12.34375 Z M 17 24 L 17 38 L 33 38 L 33 24 Z M 19 26 L 24 26 L 24 28 L 19 28 Z M 26 26 L 31 26 L 31 28 L 26 28 Z M 19 30 L 24 30 L 24 32 L 19 32 Z M 26 30 L 31 30 L 31 32 L 26 32 Z M 19 34 L 24 34 L 24 36 L 19 36 Z M 26 34 L 31 34 L 31 36 L 26 36 Z"/>
          </svg>
          <span>
            &nbsp;&nbsp;Export Applications
          </span>
        </button>
        <div className='dropdown-menu exportApplicationsDropDown' aria-labelledby="exportApplicationsDropDownToggle" onClick={handleDropdownClick}>
          <div className='buttonContainer'>
            <button className='exportApplications' onClick={createExcelSheet} disabled={isExportingSheet}>
              Export Data
            </button>
          </div>
        </div>
        
      </div>
      <div className='group'>
        <div className='applicationsDistribution'>
          {shouldRenderCharts && <CanvasJSChart options = {applicationsDistributionDonutOptions}/>}
        </div>
        <div className='applicationsDistributionCount'>
          <div className='patentCount container'>
            <div className='title'>Patent Applications</div>
            <div className='num'>{applicationsData.totalPatentApplications}</div>
          </div>
          <div className='trademarkCount container'>
            <div className='title'>Trademark Applications</div>
            <div className='num'>{applicationsData.totalTrademarkApplications}</div>
          </div>
        </div>
      </div>
      <div className='reminders'>
        <div className='title1'>Reminders Sent <small>(Last 7 days)</small></div>
        <div className='remindersContainer'>
          {applicationsData.recentReminders.length === 0 ?
            <div className='noReminders'>No Reminders Sent Recently</div>
          :
          <>
          {applicationsData.recentReminders.map((reminder, idx) => (
            <ClientApplicationsSummaryReminderItem key={idx} reminderItem={reminder} />
          ))}
          </>
          }
        </div>
      </div>
      <div className='annualApplicationsInfo'>
        <div className='title1'>Monthly Data</div>
        {shouldRenderCharts && <CanvasJSChart options = {annualDataAreaChartOptions} />}
      </div>
      <div className='group'>
        <div className='applicationsDistribution'>
          <div className='title1'>Patent Distribution</div>
          {shouldRenderCharts && <CanvasJSChart options = {patentApplicationsByTypePieChartOptions}/>}
        </div>
        <div className='applicationsDistribution'>
          <div className='title1'>Trademark Distribution</div>
          {shouldRenderCharts && <CanvasJSChart options = {trademarkApplicationsByTypePieChartOptions}/>}
        </div>
      </div>
    </div>
  )
}
